import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import Header from "@/components/Header";
import BottomNavigation from "@/components/BottomNavigation";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { WeeklyActivity, WorkoutSummary } from "@shared/schema";
import { formatDate } from "@/lib/dateUtils";

interface TimeframeProgressProps {
  workouts: WorkoutSummary[];
  timeframe: "week" | "month" | "year" | "all";
}

const TimeframeProgress: React.FC<TimeframeProgressProps> = ({
  workouts,
  timeframe,
}) => {
  const [filteredWorkouts, setFilteredWorkouts] = useState<WorkoutSummary[]>(
    []
  );

  useEffect(() => {
    if (!workouts?.length) {
      setFilteredWorkouts([]);
      return;
    }

    const today = new Date();
    let startDate = new Date();

    // Set starting date based on timeframe
    if (timeframe === "week") {
      // Get first day of current week (Monday)
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Sunday being 0
      startDate.setDate(today.getDate() - diff);
    } else if (timeframe === "month") {
      startDate.setDate(1); // First day of current month
    } else if (timeframe === "year") {
      startDate.setMonth(0, 1); // First day of current year
    } else {
      // For "all", no filtering needed
      setFilteredWorkouts(workouts);
      return;
    }

    // Reset time to midnight for comparison
    startDate.setHours(0, 0, 0, 0);

    // Filter workouts for the timeframe
    const filtered = workouts.filter((workout) => {
      const workoutDate = new Date(workout.date);
      return workoutDate >= startDate;
    });

    setFilteredWorkouts(filtered);
  }, [workouts, timeframe]);

  // Calculate metrics
  const totalWorkouts = filteredWorkouts.length;
  const totalExercises = filteredWorkouts.reduce(
    (sum, workout) => sum + workout.totalExercises,
    0
  );
  const completedExercises = filteredWorkouts.reduce(
    (sum, workout) => sum + workout.completedExercises,
    0
  );

  const completionRate =
    totalExercises > 0
      ? Math.round((completedExercises / totalExercises) * 100)
      : 0;

  const jokerDaysUsed = filteredWorkouts.filter(
    (workout) => workout.isJokerDay
  ).length;

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-primary">{totalWorkouts}</p>
              <p className="text-xs text-gray-500 mt-1">Workouts</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-primary">
                {completedExercises}/{totalExercises}
              </p>
              <p className="text-xs text-gray-500 mt-1">Exercises</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-green-500">
                {completionRate}%
              </p>
              <p className="text-xs text-gray-500 mt-1">Completion</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-500">
                {jokerDaysUsed}
              </p>
              <p className="text-xs text-gray-500 mt-1">Joker Days</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const Progress: React.FC = () => {
  const [userId, setUserId] = useState<number | null>(null);
  const [activeTimeframe, setActiveTimeframe] = useState<
    "week" | "month" | "year" | "all"
  >("week");

  // Fetch or create default user
  const { data: userData } = useQuery<{ id: number }>({
    queryKey: ["/api/default-user"],
    queryFn: async () => {
      const res = await fetch("/api/default-user", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch user");
      const data = await res.json();
      return data;
    },
  });

  // Set user ID when data is available
  React.useEffect(() => {
    if (userData?.id) {
      setUserId(userData.id);
    }
  }, [userData]);

  // Fetch weekly activity with better error handling and logging
  const {
    data: weeklyActivity,
    isLoading: isLoadingActivity,
    error: activityError,
    isFetching: isFetchingActivity,
  } = useQuery<WeeklyActivity[]>({
    queryKey: ["/api/users", userId, "weekly-activity"],
    queryFn: async () => {
      try {
        const res = await fetch(
          `/api/users/${userId}?operation=weekly-activity`,
          { credentials: "include" }
        );

        if (!res.ok) {
          throw new Error(`Failed to fetch activity: ${res.status}`);
        }
        return res.json();
      } catch (error) {
        console.error("Weekly activity fetch error:", error);
        throw error;
      }
    },
    staleTime: 0, // Consider data always stale to trigger refetch
    gcTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window regains focus
    enabled: !!userId,
  });

  // Fetch workout history
  const {
    data: workouts,
    isLoading: isLoadingWorkouts,
    isFetching: isFetchingWorkouts,
  } = useQuery<WorkoutSummary[]>({
    queryKey: ["/api/users", userId, "workouts"],
    queryFn: async () => {
      const res = await fetch(`/api/users/${userId}?operation=workouts`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch workouts");
      return res.json();
    },
    staleTime: 0, // Consider data always stale to trigger refetch
    gcTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window regains focus
    enabled: !!userId,
  });

  // Sort workouts by date (newest first)
  const sortedWorkouts = React.useMemo(() => {
    if (!workouts) return [];
    return [...workouts].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [workouts]);

  // Calculate loading states for both initial load and background refreshes
  const isLoading = isLoadingActivity || isLoadingWorkouts || !userId;
  const isRefetching = isFetchingActivity || isFetchingWorkouts;

  // Prevent showing stale data during refetch
  const showLoadingState = isLoading || isRefetching;
  const showWeeklyActivity =
    weeklyActivity && weeklyActivity.length > 0 && !showLoadingState;
  const showWorkoutData =
    sortedWorkouts && sortedWorkouts.length > 0 && !showLoadingState;

  return (
    <div className="max-w-md mx-auto min-h-screen bg-gray-50 shadow-lg relative">
      <Header title="Progress" subtitle="Your fitness journey" />

      <div
        className="p-4 overflow-y-auto pb-20"
        style={{ maxHeight: "calc(100vh - 72px)" }}
      >
        {showLoadingState ? (
          // Full loading state
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
            <p className="text-gray-500">Loading your progress data...</p>
          </div>
        ) : (
          // Content when loaded
          <Card className="bg-white mb-4">
            <CardHeader className="pb-2">
              <CardTitle>Progress Summary</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <Tabs
                defaultValue="week"
                value={activeTimeframe}
                onValueChange={(value) => setActiveTimeframe(value as any)}
                className="w-full"
              >
                <TabsList className="grid grid-cols-4 mb-4">
                  <TabsTrigger value="week">Week</TabsTrigger>
                  <TabsTrigger value="month">Month</TabsTrigger>
                  <TabsTrigger value="year">Year</TabsTrigger>
                  <TabsTrigger value="all">All</TabsTrigger>
                </TabsList>

                {showWorkoutData ? (
                  <>
                    <TabsContent value="week">
                      <TimeframeProgress
                        workouts={sortedWorkouts}
                        timeframe="week"
                      />
                    </TabsContent>
                    <TabsContent value="month">
                      <TimeframeProgress
                        workouts={sortedWorkouts}
                        timeframe="month"
                      />
                    </TabsContent>
                    <TabsContent value="year">
                      <TimeframeProgress
                        workouts={sortedWorkouts}
                        timeframe="year"
                      />
                    </TabsContent>
                    <TabsContent value="all">
                      <TimeframeProgress
                        workouts={sortedWorkouts}
                        timeframe="all"
                      />
                    </TabsContent>
                  </>
                ) : (
                  <div className="py-8">
                    <div className="grid grid-cols-2 gap-3">
                      <Skeleton className="h-24" />
                      <Skeleton className="h-24" />
                      <Skeleton className="h-24" />
                      <Skeleton className="h-24" />
                    </div>
                  </div>
                )}
              </Tabs>
            </CardContent>
          </Card>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default Progress;
